import { nextTick } from 'vue';
import type { AuthenticationResultType } from '@aws-sdk/client-cognito-identity-provider';
import {
  AssociateSoftwareTokenCommand,
  AuthFlowType,
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  SetUserMFAPreferenceCommand,
  SignUpCommand,
  VerifySoftwareTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { useRouter } from 'vue-router';
import { jwtDecode } from 'jwt-decode';

import { fromBase64Url, toBase64Url } from '@/utils/helper';
import type {
  AuthDTO,
  ChangePasswordDTO,
  CognitoProviderDTO,
  PasswordResetCodeDTO,
  PasswordResetDTO,
  RegisterDTO,
  VerifyMfaDTO,
  VerifyOtpDTO,
} from '@/types/Auth';
import { buildHostedUiUrl } from '@/utils/awsAuth';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

import { AWS_COGNITO_CLIENT_ID, AWS_FEDERATED_LOGIN_REDIRECT_URI, AWS_REGION, OTP_TIMESTAMP_KEY, PKCE_CODE_VERIFIER_KEY } from '@/utils/constants';

import { checkUserExist, checkUserProfileExist, cognitoGetToken, seedUser } from '@/services/auth';
import type { DecodedToken } from '@/types/DecodedToken';

const cognitoClient = new CognitoIdentityProviderClient({ region: AWS_REGION });

export function useAuth() {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const router = useRouter();

  let isSubmitting = false;
  let isSeedSubmitting = false;

  const signUp = async (dto: RegisterDTO) => {
    try {
      authStore.isLoading = true;
      // Create user in Cognito
      const command = new SignUpCommand({
        ClientId: AWS_COGNITO_CLIENT_ID,
        Username: dto.email,
        Password: dto.password,
        UserAttributes: [
          { Name: 'email', Value: dto.email },
          { Name: 'custom:role', Value: 'user' },
          { Name: 'custom:tier', Value: 'free' },
        ],
      });
      const registerResult = await cognitoClient.send(command);

      if (registerResult) {
        // Save session token if present
        if (registerResult.UserSub && registerResult.Session) {
          authStore.setRegisterInfo({
            token: registerResult.Session,
            email: dto.email,
            password: dto.password,
          });
          nextTick(() => {
            // Redirect to OTP verification page
            authStore.isOtpSetup = true;
            // Set OTP timestamp when redirecting to verify page
            localStorage.setItem(OTP_TIMESTAMP_KEY, Date.now().toString());
            router.push('/verify');
          });
        }
      } else {
        throw new Error('Error signing up user');
      }
    } catch (err) {
      throw new Error(err);
    } finally {
      authStore.isLoading = false;
    }
  };

  const confirmOtp = async ({ email, otp }: VerifyOtpDTO) => {
    // @TODO: remove this if not necesarry anymore
    if (isSubmitting) return; // Prevent multiple submissions
    isSubmitting = true;

    try {
      authStore.isLoading = true;

      const command = new ConfirmSignUpCommand({
        ClientId: AWS_COGNITO_CLIENT_ID,
        Username: email,
        ConfirmationCode: otp,
      });

      const confirmOTP = await cognitoClient.send(command);

      if (!confirmOTP) throw new Error('OTP confirmation failed');

      if (confirmOTP && confirmOTP.Session) {
        const password = authStore.password ?? '';
        const loginResult = await loginAfterOtp(email, password);

        if (!loginResult || !loginResult.IdToken) throw new Error('Login failed: No token returned');

        if (loginResult && loginResult.IdToken) {
          const idToken = loginResult.IdToken;
          const awsAccessToken = loginResult.AccessToken;
          authStore.clearRegisterInfo();
          authStore.setAccessToken(idToken);
          authStore.setAWSAccessToken(awsAccessToken ?? '');

          const seedUserResult = await seedUserProfile();

          if (!seedUserResult) throw new Error('Seeding User failed');
          if (seedUserResult) {
            nextTick(() => {
              router.push('/security-setup');
            });
          }
        }
      }
    } catch (err) {
      console.error(err);
      throw new Error('OTP confirmation failed');
    } finally {
      isSubmitting = false;
      authStore.isLoading = false;
    }
  };

  const resendOtp = async (email: string) => {
    try {
      authStore.sendingVerify = true;
      const command = new ResendConfirmationCodeCommand({
        ClientId: AWS_COGNITO_CLIENT_ID,
        Username: email,
      });
      await cognitoClient.send(command);
    } catch (err) {
      console.error(err);
      throw new Error('OTP resend failed');
    } finally {
      isSubmitting = false;
      authStore.sendingVerify = false;
    }
  };

  const loginAfterOtp = async (email: string, password: string) => {
    try {
      authStore.isLoading = true;

      const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: AWS_COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const result = await cognitoClient.send(command);

      return result.AuthenticationResult; // contains IdToken, AccessToken, RefreshToken, Session
    } catch (err) {
      console.error('Error logging in after OTP:', err);
      throw new Error('Login failed');
    } finally {
      authStore.isLoading = false;
    }
  };

  const seedUserProfile = async () => {
    // @TODO: remove this if not necesarry anymore
    if (isSeedSubmitting) return; // Prevent multiple submissions
    isSeedSubmitting = true;

    try {
      userStore.isLoading = true;

      const seedProfileResult = await seedUser();

      return seedProfileResult;
    } catch (err) {
      console.error(err);
      throw new Error('Profile seeding failed');
    } finally {
      isSeedSubmitting = false;
      userStore.isLoading = false;
    }
  };

  const setupTotpMfa = async () => {
    try {
      const command = new AssociateSoftwareTokenCommand({
        AccessToken: authStore.awsAccessToken ?? '',
      });

      const response = await cognitoClient.send(command);
      const secretCode = response.SecretCode!;

      return `otpauth://totp/${encodeURIComponent(authStore.email || 'user')}?secret=${secretCode}&issuer=Casim - E-Invoicing`;
    } catch (err) {
      throw new Error('Failed to generate secret. ' + err.message);
    }
  };

  const verifyTotpCode = async (totpCode: string) => {
    try {
      authStore.isLoading = true;
      const command = new VerifySoftwareTokenCommand({
        AccessToken: authStore.awsAccessToken ?? '',
        UserCode: totpCode,
        FriendlyDeviceName: 'Casim - E-Invoicing',
      });

      const response = await cognitoClient.send(command);

      if (response.Status === 'SUCCESS') {
        const verifyTotpResult = await cognitoClient.send(
          new SetUserMFAPreferenceCommand({
            AccessToken: authStore.awsAccessToken ?? '',
            SoftwareTokenMfaSettings: {
              Enabled: true,
              PreferredMfa: true,
            },
            SMSMfaSettings: {
              Enabled: false,
              PreferredMfa: false,
            },
            EmailMfaSettings: {
              Enabled: false,
              PreferredMfa: false,
            },
          }),
        );

        authStore.clearMFAInfo();

        if (verifyTotpResult) {
          nextTick(() => {
            if (authStore.isInvitedRegistration) {
              router.push('/member-setup');
            } else {
              router.push('/profile-setup');
            }
          });
        } else {
          throw new Error('Verification failed');
        }
      } else {
        throw new Error('Invalid Code. Please try again.');
      }
    } catch (err) {
      throw new Error('Verification failed: ' + err.message);
    } finally {
      authStore.isLoading = false;
    }
  };

  const login = async ({ email, password }: AuthDTO) => {
    try {
      authStore.isLoading = true;
      const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: AWS_COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const result = await cognitoClient.send(command);

      if (result.ChallengeName) {
        // User has 2FA enabled, so we get a challenge
        if (result.ChallengeName === 'SOFTWARE_TOKEN_MFA' || result.ChallengeName === 'SMS_MFA') {
          authStore.setCognitoMfaAccessToken(result.Session ?? '');
          return {
            challengeName: result.ChallengeName,
            friendlyDeviceName: result.ChallengeParameters?.FRIENDLY_DEVICE_NAME,
            email,
          };
        }
      }

      if (!result.AuthenticationResult) throw new Error('Login failed: No authentication result');

      const idToken = result.AuthenticationResult?.IdToken;

      if (!idToken) throw new Error('Login failed: No token returned');

      // Store tokens properly
      authStore.setAccessToken(idToken); // This is the ID token for your app
      console.log(result.AuthenticationResult.AccessToken);
      authStore.setAWSAccessToken(result.AuthenticationResult.AccessToken ?? ''); // This is for Cognito API calls

      // Decode token to extract user data
      const decoded = jwtDecode<DecodedToken>(idToken);
      authStore.setEmail(decoded.email);

      if (!result.ChallengeName) {
        // Set tokens here because we need to force the user to set up their MFA/2FA
        nextTick(async () => {
          router.push('/security-setup');
        });
      } else {
        nextTick(async () => {
          router.push('/');
        });
      }
    } catch (err) {
      console.error('Login error:', err);
      throw new Error('Invalid email or password');
    } finally {
      authStore.isLoading = false;
    }
  };

  const verifyMfa = async (payload: VerifyMfaDTO) => {
    try {
      if (!authStore.cognitoMfaAccessToken) {
        throw new Error('MFA access token is required for verification');
      }

      authStore.isLoading = true;

      const challengeResponses = {
        USERNAME: payload.email,
        SOFTWARE_TOKEN_MFA_CODE: payload.mfaCode, // or 'SMS_MFA_CODE'
      };

      const command = new RespondToAuthChallengeCommand({
        ChallengeName: 'SOFTWARE_TOKEN_MFA', // or 'SMS_MFA'
        ClientId: AWS_COGNITO_CLIENT_ID,
        Session: authStore.cognitoMfaAccessToken ?? '',
        ChallengeResponses: challengeResponses,
      });

      const result = await cognitoClient.send(command);

      if (!result.AuthenticationResult) throw new Error('Login failed: No authentication result');

      const authResult = result.AuthenticationResult as AuthenticationResultType;
      const idToken = authResult?.IdToken;
      if (!idToken) throw new Error('Login failed: No token returned');

      // Store tokens properly
      authStore.setAccessToken(idToken); // This is the ID token for your app
      authStore.setAWSAccessToken(authResult.AccessToken ?? ''); // This is for Cognito API calls
      authStore.clearCognitoMfaAccessToken();
      const decoded = jwtDecode<DecodedToken>(idToken);
      authStore.setEmail(decoded.email);
      nextTick(async () => {
        router.push('/');
      });
    } catch (err) {
      console.error('MFA verification failed:', err);
      throw new Error(err);
    } finally {
      authStore.isLoading = false;
    }
  };

  const logout = async () => {
    try {
      if (authStore.accessToken && authStore.authType === 'email') {
        const command = new GlobalSignOutCommand({
          AccessToken: authStore.awsAccessToken ?? '',
        });
        await cognitoClient.send(command);
      }

      if (authStore.accessToken && authStore.authType === 'microsoft') {
        const federatedWebLogoutUrl = await buildHostedUiUrl('logout');
        window.location.assign(federatedWebLogoutUrl);
      }

      authStore.logout();

      if (authStore.authType === 'email') {
        router.push('/login');
      }
    } catch (err) {
      // Even if the API call fails (e.g., token expired), we should still clear local state
      // and log the user out of the application from a UI perspective.
      authStore.logout();
      router.push('/login');
      console.error('Logout error:', err);
      throw new Error('Logout failed');
    }
  };

  const handleSSOCallback = async (code: string) => {
    try {
      authStore.isLoading = true;
      const params = {
        grant_type: 'authorization_code',
        client_id: AWS_COGNITO_CLIENT_ID,
        code,
        redirect_uri: AWS_FEDERATED_LOGIN_REDIRECT_URI,
        code_verifier: sessionStorage.getItem(PKCE_CODE_VERIFIER_KEY) || '',
      } as CognitoProviderDTO;
      // Exchange the authorization code for tokens
      const result = await cognitoGetToken(params);
      // const result = await cognitoGetTokenMutation().mutateAsync({ code });

      if (result) {
        authStore.setFederatedAccessToken(result.id_token);
        authStore.authType = 'microsoft';

        const checkUserResult = await checkUserProfileExist(result.id_token);

        // Set the token here so the user will not redirected to login
        authStore.setAccessToken(result.id_token);
        authStore.setAWSAccessToken(result.access_token);
        authStore.clearFederatedAccessToken();

        if ('exists' in checkUserResult && !checkUserResult.exists && !checkUserResult.mfaSetup) {
          // Redirect user to profile se
          nextTick(() => {
            router.push('/profile-setup');
          });
        } else {
          console.log('here');
          authStore.clearFederatedAccessToken();
          router.push('/');
        }
      }
    } catch (err) {
      // authStore.logout();
      console.error('SSO callback error:', err);
      throw new Error('SSO callback failed');
    } finally {
      authStore.isLoading = false;
    }
  };

  /**
   * Initiates the Passkey (WebAuthn) registration process.
   * This function interacts with Cognito's Custom Auth flow to register a new passkey.
   *
   * @param {string} email The user's email.
   * @param {string} friendlyDeviceName A user-friendly name for the passkey (e.g., 'My Laptop Passkey').
   * @returns {Promise<object|null>} The AuthenticationResult on successful registration and login, or null if another challenge is required.
   */
  const registerPasskey = async (email: string, friendlyDeviceName: string) => {
    try {
      // Initiate the authentication flow to get the WebAuthn challenge from Cognito
      // This sends a request to Cognito, which triggers your DefineAuthChallenge Lambda
      // to ask for a CUSTOM_CHALLENGE (WebAuthn registration).
      const initiateAuthCommand = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.CUSTOM_AUTH,
        ClientId: AWS_COGNITO_CLIENT_ID,
        AuthParameters: {
          // You might need to send a specific parameter to indicate passkey registration
          // For example, 'CHALLENGE_NAME': 'WEBAUTHN_REGISTER'
          // Or, your DefineAuthChallenge Lambda might determine it based on user state.
          CHALLENGE_NAME: 'WEBAUTHN_REGISTER',
          USERNAME: email, // Replace with the actual username/email
          // Other parameters as needed by your custom auth flow Lambdas
        },
      });

      const initiateAuthResponse = await cognitoClient.send(initiateAuthCommand);

      if (initiateAuthResponse.ChallengeName === 'CUSTOM_CHALLENGE' && initiateAuthResponse.ChallengeParameters) {
        // Parse the WebAuthn challenge from Cognito's response
        // Your CreateAuthChallenge Lambda should return this in ChallengeParameters.
        // It will likely be a JSON string that needs to be parsed.
        const challengeParams = initiateAuthResponse.ChallengeParameters;
        const webAuthnChallengeJson = challengeParams.webAuthnChallenge; // Key name depends on your Lambda output

        if (!webAuthnChallengeJson) {
          throw new Error('No WebAuthn challenge found in Cognito response.');
        }

        const credentialCreationOptions = JSON.parse(webAuthnChallengeJson);

        // Convert ArrayBuffers from base64url where necessary
        credentialCreationOptions.challenge = fromBase64Url(credentialCreationOptions.challenge);
        credentialCreationOptions.user.id = fromBase64Url(credentialCreationOptions.user.id);

        if (credentialCreationOptions.excludeCredentials) {
          credentialCreationOptions.excludeCredentials = credentialCreationOptions.excludeCredentials.map((cred: any) => ({
            ...cred,
            id: fromBase64Url(cred.id),
          }));
        }

        // Request passkey creation from the browser (WebAuthn API)
        const credential = (await navigator.credentials.create({
          publicKey: credentialCreationOptions,
        })) as PublicKeyCredential;

        if (!credential) {
          throw new Error('Passkey creation failed or was cancelled.');
        }

        const attestationObject = credential.response instanceof AuthenticatorAttestationResponse ? credential.response.attestationObject : null;
        const clientDataJSON = credential.response.clientDataJSON;
        const rawId = credential.rawId;

        if (!attestationObject || !clientDataJSON || !rawId) {
          throw new Error('Invalid WebAuthn credential response.');
        }

        const respondCommand = new RespondToAuthChallengeCommand({
          ChallengeName: 'CUSTOM_CHALLENGE',
          ClientId: AWS_COGNITO_CLIENT_ID,
          Session: initiateAuthResponse.Session, // Crucial for maintaining the session
          ChallengeResponses: {
            USERNAME: email, // Replace with actual username/email
            CHALLENGE_NAME: 'WEBAUTHN_REGISTER', // Inform your Lambda about the action
            // Send the raw WebAuthn response components as base64url encoded strings
            WEBAUTHN_RESPONSE_ATT_OBJ: toBase64Url(attestationObject),
            WEBAUTHN_RESPONSE_CLIENT_DATA_JSON: toBase64Url(clientDataJSON),
            WEBAUTHN_RESPONSE_RAW_ID: toBase64Url(rawId),
            // You might send other info like friendlyDeviceName here
            FRIENDLY_DEVICE_NAME: friendlyDeviceName,
          },
        });

        const response = await cognitoClient.send(respondCommand);
        console.log('Passkey registration response:', response);

        if (response.AuthenticationResult) {
          // Passkey registered and user signed in/session updated
          console.log('Passkey registered successfully!');
          // Store new tokens if provided in AuthenticationResult
          // authStore.accessToken = response.AuthenticationResult.AccessToken;
          // authStore.idToken = response.AuthenticationResult.IdToken;
          // authStore.refreshToken = response.AuthenticationResult.RefreshToken;
          return response.AuthenticationResult;
        } else if (response.ChallengeName) {
          // Another challenge might be required (e.g., MFA after passkey registration)
          console.warn('Another challenge required:', response.ChallengeName);
          // Handle subsequent challenges here
        } else {
          throw new Error('Passkey registration failed with an unknown status.');
        }
      } else {
        throw new Error('Unexpected challenge from Cognito during passkey registration initiation.');
      }
    } catch (error: any) {
      console.error('Error registering passkey:', error);
      throw new Error('Failed to register passkey: ' + error.message);
    }
  };

  /**
   * Initiates the Passkey (WebAuthn) login process.
   * Your Lambda functions will return the WebAuthn challenge parameters.
   * @param username The user's username or email.
   */
  const signInWithPasskey = async (email: string) => {
    try {
      // 1. Initiate the authentication flow to get the WebAuthn challenge from Cognito
      const initiateAuthCommand = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.CUSTOM_AUTH,
        ClientId: AWS_COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          CHALLENGE_NAME: 'WEBAUTHN_LOGIN', // Inform your Lambda about the action
        },
      });

      const initiateAuthResponse = await cognitoClient.send(initiateAuthCommand);

      if (initiateAuthResponse.ChallengeName === 'CUSTOM_CHALLENGE' && initiateAuthResponse.ChallengeParameters) {
        // 2. Parse the WebAuthn challenge from Cognito's response
        const challengeParams = initiateAuthResponse.ChallengeParameters;
        const webAuthnChallengeJson = challengeParams.webAuthnChallenge; // Key name depends on your Lambda output

        if (!webAuthnChallengeJson) {
          throw new Error('No WebAuthn challenge found in Cognito response.');
        }

        const credentialRequestOptions = JSON.parse(webAuthnChallengeJson);

        // Convert ArrayBuffers from base64url where necessary
        credentialRequestOptions.challenge = fromBase64Url(credentialRequestOptions.challenge);
        if (credentialRequestOptions.allowCredentials) {
          credentialRequestOptions.allowCredentials = credentialRequestOptions.allowCredentials.map((cred: any) => ({
            ...cred,
            id: fromBase64Url(cred.id),
          }));
        }

        // 3. Request passkey assertion from the browser (WebAuthn API)
        const credential = (await navigator.credentials.get({
          publicKey: credentialRequestOptions,
        })) as PublicKeyCredential;

        if (!credential) {
          throw new Error('Passkey authentication failed or was cancelled.');
        }

        // 4. Send the asserted passkey data back to Cognito for verification
        const authenticatorData = credential.response instanceof AuthenticatorAssertionResponse ? credential.response.authenticatorData : null;
        const clientDataJSON = credential.response.clientDataJSON;
        const signature = credential.response instanceof AuthenticatorAssertionResponse ? credential.response.signature : null;
        const userHandle = credential.response instanceof AuthenticatorAssertionResponse ? credential.response.userHandle : null;
        const rawId = credential.rawId;

        if (!authenticatorData || !clientDataJSON || !signature || !rawId) {
          throw new Error('Invalid WebAuthn credential assertion response.');
        }

        const respondCommand = new RespondToAuthChallengeCommand({
          ChallengeName: 'CUSTOM_CHALLENGE',
          ClientId: AWS_COGNITO_CLIENT_ID,
          Session: initiateAuthResponse.Session,
          ChallengeResponses: {
            USERNAME: email,
            CHALLENGE_NAME: 'WEBAUTHN_LOGIN', // Inform your Lambda about the action
            // Send the raw WebAuthn response components as base64url encoded strings
            WEBAAUTHN_RESPONSE_AUTH_DATA: toBase64Url(authenticatorData),
            WEBAUTHN_RESPONSE_CLIENT_DATA_JSON: toBase64Url(clientDataJSON),
            WEBAUTHN_RESPONSE_SIGNATURE: toBase64Url(signature),
            WEBAUTHN_RESPONSE_RAW_ID: toBase64Url(rawId),
            // userHandle is optional but useful if available
            ...(userHandle && { WEBAUTHN_RESPONSE_USER_HANDLE: toBase64Url(userHandle) }),
          },
        });

        const response = await cognitoClient.send(respondCommand);
        console.log('Passkey login response:', response);

        if (response.AuthenticationResult) {
          // User successfully signed in with passkey
          console.log('Successfully signed in with passkey!');
          // Store tokens
          // authStore.accessToken = response.AuthenticationResult.AccessToken;
          // authStore.idToken = response.AuthenticationResult.IdToken;
          // authStore.refreshToken = response.AuthenticationResult.RefreshToken;
          return response.AuthenticationResult;
        } else if (response.ChallengeName) {
          console.warn('Another challenge required after passkey login:', response.ChallengeName);
          // This might happen if you have multi-factor authentication setup
          // For example, if passkey is a "first factor" and TOTP is a "second factor"
          // You would then initiate a new challenge response for TOTP.
        } else {
          throw new Error('Passkey login failed with an unknown status.');
        }
      } else {
        throw new Error('Unexpected challenge from Cognito during passkey login initiation.');
      }
    } catch (error: any) {
      console.error('Error signing in with passkey:', error);
      throw new Error('Failed to sign in with passkey: ' + error.message);
    }
  };

  const sendPasswordResetCode = async (payload: PasswordResetCodeDTO) => {
    try {
      const command = new ForgotPasswordCommand({
        ClientId: AWS_COGNITO_CLIENT_ID,
        Username: payload.email,
      });

      const verificationCodeResult = await cognitoClient.send(command);

      if (verificationCodeResult) {
        return verificationCodeResult;
      } else {
        throw new Error('Error sending Password Reset code');
      }
    } catch (err) {
      console.error('Change password failed:', err);
      throw new Error(err.message || 'Failed to change password.');
    }
  };

  const confirmForgotPassword = async (payload: PasswordResetDTO) => {
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: AWS_COGNITO_CLIENT_ID,
        Username: payload.email,
        ConfirmationCode: payload.verificationCode,
        Password: payload.newPassword,
      });

      const verificationCodeResult = await cognitoClient.send(command);

      if (verificationCodeResult) {
        return verificationCodeResult;
      } else {
        throw new Error('Error assigning new password to user');
      }
    } catch (err) {
      console.error('Change password failed:', err);
      throw new Error(err.message || 'Failed to change password.');
    }
  };

  const changePassword = async (payload: ChangePasswordDTO) => {
    if (!authStore.accessToken) {
      throw new Error('User is not logged in.');
    }

    try {
      const command = new ChangePasswordCommand({
        AccessToken: authStore.accessToken,
        PreviousPassword: payload.currentPassword,
        ProposedPassword: payload.newPassword,
      });

      const changePasswordResult = await cognitoClient.send(command);

      if (changePasswordResult) {
        return changePasswordResult;
      } else {
        throw new Error('Error assigning new password to user');
      }
    } catch (err) {
      err.error('Change password failed:', err);
      throw new Error(err.message || 'Failed to change password.');
    }
  };

  /**
   * Checks if a user exists in the system by email address
   * @param email The email address to check
   * @returns Promise resolving to a boolean indicating if the user exists
   * @throws Error if the API request fails
   */
  const checkIfUserExists = async (email: string): Promise<boolean> => {
    try {
      const response = await checkUserExist(email);
      return response.exists;
    } catch (err) {
      console.error('Error checking if user exists:', err);
      throw new Error('Failed to verify email. Please try again.');
    }
  };

  return {
    signUp,
    confirmOtp,
    resendOtp,
    login,
    logout,
    setupTotpMfa,
    verifyTotpCode,
    handleSSOCallback,
    registerPasskey,
    signInWithPasskey,
    sendPasswordResetCode,
    confirmForgotPassword,
    changePassword,
    verifyMfa,
    checkIfUserExists,
  };
}
